import React, { useState } from "react";
import Layout from "../Componants/Layout";
import { Button, Col, Container, Row } from "react-bootstrap";
import Form from "react-bootstrap/Form";
import { useFormik } from "formik";
import * as Yup from "yup";
import StarRating from "../Componants/StarRating";
import { useMutation, useQuery } from "@tanstack/react-query";
import { getRequest, postRequest } from "../services/apiMethods";
import { useLocation, useNavigate } from "react-router-dom";
import { filterTheArray, getQueryParams } from "../utils/HelperFunctions";
import Select from "react-select";
import toast from "react-hot-toast";

const customStyles = {
  control: (provided) => ({
    ...provided,
    padding: "4px",
    minHeight: "45px",
  }),
};

export default function LeaveReview() {
  const navigate = useNavigate();
  const [rating, setRating] = useState(0);
  const location = useLocation();
  const queryParams = getQueryParams(location?.search);
  const { leadId, vendorId } = queryParams;
  const {
    data: CustomerEnquiriesDetailData,
    error,
    isLoading,
    refetch,
  } = useQuery({
    queryKey: ["CustomerEnquiriesDetail"],
    queryFn: () => getRequest(`/lead/info?id=${leadId}`),
    enabled: !!leadId,
  });

  const enquiryDetail = CustomerEnquiriesDetailData?.data;

  const { data: electricianProfile } = useQuery({
    queryKey: ["electricianProfile"],
    queryFn: () => {
      const url = `/profile/favourite/vendor?vendorId=${vendorId}`;
      return getRequest(url);
    },
    enabled: !!vendorId,
  });

  const { data: categoryList } = useQuery({
    queryKey: ["categories"],
    queryFn: () => getRequest("/auth/view-category"),
  });

  const filteredCategories = Array.isArray(categoryList?.data)
    ? categoryList.data.filter((category) =>
      electricianProfile?.data?.vendorServices.some((vendorService) =>
        vendorService.category._id === category.categoryId &&
        vendorService.services.some(
          (service) => service._id === category.serviceId
        )
      )
    )
    : [];

  const options = Array.isArray(filteredCategories)
    ? filteredCategories.map((item) => ({
      value: item.serviceId,
      label: item.serviceName,
      category: item.categoryId,
    }))
    : [];

  const initialValues = {
    comment: "",
    rating: "",
    services: null,
  };

  const validationSchema = Yup.object({
    comment: Yup.string().required("Comment is required"),
    rating: Yup.number()
      .required("Rating is required")
      .min(1, "Rating must be at least 1")
      .max(5, "Rating must be at most 5"),
  });

  const { mutate, isLoading: apiState } = useMutation({
    mutationFn: (data) => postRequest(`/review/create`, data),
    onSuccess: (data) => {
      // toast.success(`${data?.message}`, {
      toast.success(`Review submitted successfully`, {
        iconTheme: {
          primary: "#337FE0",
          secondary: "#FFFAEE",
        },
      });
      navigate(-1);
    },
    onError: (error) => {
      console.error("Error during API call:", error);
    },
  });

  const formik = useFormik({
    initialValues,
    validationSchema,
    onSubmit: (values) => {
      if (enquiryDetail?.category?._id && enquiryDetail?.service?._id) {
        const payload = {
          comment: values.comment,
          rating: values.rating,
          vendor: vendorId,
          category: enquiryDetail?.category?._id,
          service: enquiryDetail?.service?._id,
          lead: leadId
        };
        mutate(payload);
      }

    },
  });

  return (
    <Layout>
      <Container>
        <div className="dashboard-main">
          <h3 className="with-back-btn cursor-pointer" onClick={() => navigate(-1)}>
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path
                d="M4 12L3.29289 11.2929L2.58579 12L3.29289 12.7071L4 12ZM19 13C19.5523 13 20 12.5523 20 12C20 11.4477 19.5523 11 19 11V13ZM9.29289 5.29289L3.29289 11.2929L4.70711 12.7071L10.7071 6.70711L9.29289 5.29289ZM3.29289 12.7071L9.29289 18.7071L10.7071 17.2929L4.70711 11.2929L3.29289 12.7071ZM4 13H19V11H4V13Z"
                fill="black"
              />
            </svg>
            Back
          </h3>
          <h3>Leave Review</h3>
          <Row>
            <Col lg={8}>
              <div className="my-profile-card-contain">
                <div className="my-profile-box-contain">
                  <div className="my-profile-box-form">
                    <div className="h-100 m-0">
                      <Form className="cmn-form-set" onSubmit={formik.handleSubmit}>
                        <Form.Group className="mb-3" controlId="formImage">
                          <div className="cmpny-logo-upld">
                            {electricianProfile?.data?.image ? (
                              <img
                                src={electricianProfile?.data?.image}
                                style={{ width: "100px", height: "100px" }}
                                className={electricianProfile?.data?.subscription?.plan === "Premium" ? "prime" : ""}
                              />
                            ) : (
                              <div
                                className={
                                  electricianProfile?.data?.subscription?.plan === "Premium"
                                    ? "electricianProfile-custom-circle prime"
                                    : "electricianProfile-custom-circle"
                                }
                              >
                                <span>
                                  {electricianProfile?.data?.bussinessName
                                    ? electricianProfile?.data?.bussinessName.toUpperCase().at(0)
                                    : "N"}
                                </span>
                              </div>
                            )}
                          </div>
                        </Form.Group>

                        <div className="form-box-title">
                          <h4>{electricianProfile?.data?.bussinessName}</h4>
                          <p>
                            {electricianProfile?.data?.city ||
                              `${filterTheArray(
                                electricianProfile?.data?.address
                              )}, ${electricianProfile?.data?.postCode?.slice(0, 3)}`}
                          </p>
                        </div>

                        <div className="star">
                          <span>Overall Rating</span>
                          <StarRating
                            setRating={setRating}
                            name="rating"
                            setFieldValue={formik.setFieldValue}
                            size={60}
                            initialRating={rating}
                          />
                          {formik.errors.rating && formik.touched.rating && (
                            <div className="invalid-feedback d-block">{formik.errors.rating}</div>
                          )}
                        </div>

                        <Row>
                          <Col lg={12}>
                            <Form.Group className="mb-3">
                              <Form.Label>Service</Form.Label>
                              <Form.Control
                                type="text"
                                name="services"
                                value={enquiryDetail?.service?.serviceName}
                                disabled

                              />
                              <Form.Control.Feedback type="invalid">
                                {formik.errors.name}
                              </Form.Control.Feedback>
                            </Form.Group>
                          </Col>
                          <Col lg={12}>
                            <Form.Group className="mb-3" controlId="formname">
                              <Form.Label>Describe your experience with the electrician</Form.Label>
                              <Form.Control
                                as="textarea"
                                placeholder="Type here"
                                name="comment"
                                value={formik.values.comment}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                isInvalid={formik.touched.comment && formik.errors.comment}
                              />
                              <Form.Control.Feedback type="invalid">{formik.errors.comment}</Form.Control.Feedback>
                            </Form.Group>
                          </Col>
                        </Row>

                        <div className="d-flex gap-4 mt-4">
                          <button type="submit" className="theme-comm-btn" disabled={apiState}>
                            Save
                          </button>
                        </div>
                      </Form>
                    </div>
                  </div>
                </div>
              </div>
            </Col>
            <Col lg={4}></Col>
          </Row>
        </div>
      </Container>
    </Layout>
  );
}
